import { AlertTriangle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Live price: Admin sees cost + per-piece breakdown, managers a per-piece breakdown at sell, reps only the sell total.
export function PriceBox({ price, gap, busy, saveLabel, onSave, onFit }) {
  const { user } = useAuth();
  const owner = user?.role === "owner";
  const blocked = Math.abs(gap) > 0.05;
  const groups = {};
  (price?.parts || []).forEach((p) => { (groups[p.piece ?? 0] = groups[p.piece ?? 0] || []).push(p); });
  return (
    <div className="space-y-3">
      {blocked && (
        <div data-testid="design-size-error" className="flex flex-wrap items-center gap-3 rounded-[12px] bg-[#FF3B30]/10 px-4 py-3 text-[15px] text-[#D70015]">
          <AlertTriangle className="h-4 w-4" /> Sizes don't add up — {Math.round(Math.abs(gap) * 1000) / 1000} in
          <button data-testid="design-fit-button" onClick={onFit} className="ml-auto rounded-full bg-white px-3 py-1 text-[14px] font-medium text-[#0062CC]">Fit pieces to overall size</button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-5 rounded-[14px] bg-white p-4" data-testid="design-price-box">
        {price?.error ? <p className="text-[15px] text-[#D70015]" data-testid="design-price-error">{price.error}</p> : (
          <>
            {owner && <div><p className="text-[12px] uppercase text-[#6D6D72]">Cost</p><p data-testid="design-cost" className="font-mono-num text-[22px] font-semibold text-black">{money(price?.cost)}</p></div>}
            <div><p className="text-[12px] uppercase text-[#6D6D72]">{owner ? "Suggested" : "Price"}</p><p data-testid="design-suggested" className="font-mono-num text-[22px] font-semibold text-[#0062CC]">{money(price?.suggested)}</p></div>
            <p data-testid="design-summary" className="min-w-[160px] flex-1 text-[13px] text-[#6D6D72]">{price?.summary}{price?.warn ? <span className="block text-[#D70015]">{price.warn}</span> : null}</p>
          </>
        )}
        <button data-testid="design-save-button" disabled={busy || !price || !!price.error || blocked} onClick={onSave}
          className="inline-flex h-11 items-center gap-2 rounded-full bg-[#007AFF] px-6 text-[15px] font-semibold text-white transition-colors hover:bg-[#0062CC] disabled:opacity-40">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{saveLabel}
        </button>
      </div>
      {price?.parts && (
        <div className="space-y-3 rounded-[14px] bg-white p-4 text-[13px]" data-testid="design-breakdown">
          <p className="text-[15px] font-semibold text-black">{owner ? "Cost by piece" : "Price by piece"}</p>
          {price.no_price?.length > 0 && <p data-testid="design-no-price" className="rounded-[10px] bg-[#FF9500]/12 px-3 py-2 text-[#8A4B00]">No supplier price (added as $0): {price.no_price.join(", ")}. Enter a price in Products & pricing → Builder options.</p>}
          {Object.entries(groups).map(([k, parts]) => {
            const pc = (price.pieces || []).find((x) => String(x.n) === k);
            return (
              <div key={k} data-testid={`design-breakdown-piece-${k}`}>
                <div className="flex justify-between font-semibold text-black"><span>{pc ? `${pc.name} ${pc.size} · ${pc.line}` : "Joints"}</span>{pc && <span className="font-mono-num">{money(pc.cost ?? pc.price)}</span>}</div>
                {parts.map((p, i) => (
                  <div key={i} className={`flex justify-between gap-3 py-0.5 ${p.missing ? "text-[#8A4B00]" : "text-[#3C3C43]"}`}>
                    <span>{p.label.replace(/^[^·(]*\([^)]*×[^)]*\) · /, "")}</span><span className="font-mono-num">{money(p.amount)}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
